
import {Component, Vue, Watch} from "vue-property-decorator";
import {TaskDurum} from "@/plugins/uyap-plugin/enum/TaskDurum";
import {ITask} from "@/plugins/uyap-plugin/store/modules/GorevYoneticisiTypes";
import {TaskTuru, TaskTuruLabel} from "@/plugins/uyap-plugin/enum/TaskTuru";
import DeleteDialog from "@/components/dialogs/DeleteDialog.vue";
import {ProgramPaths} from "@/enum/ProgramPaths";
import JSZip from "jszip";
import * as FileSaver from "file-saver";
import {TamamlanmayanDosyalarRow} from "@/plugins/uyap-plugin/uyap/TamamlanmayanDosyalar";

interface IHataDetayState {
  loading: boolean;
  messages: string[];
}

const DEFAULT_DETAIL_MESSAGE = "Ayrıntı için göz butonuna tıklayınız.";

const STATUS_BASE_MESSAGES: Record<string, string> = {
  BITTI_BASARILI: "Başarılı",
  BITTI_EKSIK: "Bazı görevler hatalı.",
  BITTI_HATALI: "Hata oluştu.",
  X: DEFAULT_DETAIL_MESSAGE
};

@Component({
  components: {DeleteDialog}
})

export default class TamamlananGorevList extends Vue {
  taskDurum = TaskDurum;
  taskTuruLabel = TaskTuruLabel;
  taskTuru = TaskTuru;
  isDeleteModeActive = false;
  silinecekTasks: Array<ITask> = [];
  deleting = false;
  hataDetayState: Record<number, IHataDetayState> = {};
  private hataDetayPromises: Record<number, Promise<string[]>> = {};
  disabledDownloadIds = [];

  get tasks(): Array<ITask> {
    return this.$store.getters.getTamamlananGorevler;
  }

  @Watch('tasks', {immediate: true})
  onTasksChanged(tasks: Array<ITask>) {
    this.prefetchHataDetaylari(tasks);
  }

  headers = [
    {text: "Görev Türü", value: "islemTuru"},
    {text: "Açıklama", value: "aciklama"},
    {text: "Oluşturulma Tarihi", value: "olusturulmaTarihi", sortable: false},
    {text: "Tamamlanma Tarihi", value: "tamamlanmaTarihi", sortable: false},
    {text: "Durum", value: "durum", sortable: true, align: 'center'},
    {text: "İşlemler", class: "sticky-header", value: "actions", sortable: false, width: 120, align: 'center'},
  ];

  private prefetchHataDetaylari(tasks: Array<ITask>) {
    if (!Array.isArray(tasks)) {
      return;
    }
    tasks
        .filter((task: ITask) => this.shouldFetchHataDetay(task))
        .forEach((task: ITask) => {
          void this.ensureHataMesajlari(task);
        });
  }

  hasDurumIcon(item: ITask): boolean {
    return this.getDurumIcon(item) !== null;
  }

  getDurumIcon(item: ITask): string | null {
    const status: any = item.status;
    if (status === this.taskDurum.BITTI_BASARILI) {
      return "mdi-check-circle-outline";
    }
    if (status === this.taskDurum.BITTI_EKSIK) {
      return "mdi-progress-alert";
    }
    if (status === this.taskDurum.BITTI_HATALI) {
      return "mdi-close-circle-outline";
    }
    if (typeof status === "string" && status.toUpperCase() === "X") {
      return "mdi-information-outline";
    }
    return null;
  }

  getDurumColorClass(item: ITask): string {
    const statusKey = this.getStatusKey(item.status);
    switch (statusKey) {
      case "BITTI_BASARILI":
        return "green--text text--darken-2";
      case "BITTI_EKSIK":
        return "orange--text text--darken-2";
      case "BITTI_HATALI":
        return "red--text text--darken-2";
      case "X":
        return "blue-grey--text text--darken-2";
      default:
        return "grey--text text--darken-1";
    }
  }

  handleDurumHover(item: ITask) {
    if (this.shouldFetchHataDetay(item)) {
      void this.ensureHataMesajlari(item);
    }
  }

  getDurumTooltip(item: ITask): string {
    const statusKey = this.getStatusKey(item.status);
    const baseMessage = STATUS_BASE_MESSAGES[statusKey] || "";

    if (!this.shouldFetchHataDetay(item)) {
      return baseMessage || statusKey;
    }

    const entry = item.id ? this.hataDetayState[item.id] : undefined;

    if (entry && entry.loading) {
      const loadingMessages = new Set<string>();
      if (baseMessage) {
        loadingMessages.add(baseMessage);
      }
      loadingMessages.add("Hata detayları getiriliyor...");
      return Array.from(loadingMessages).join("\n");
    }

    const detailSet = new Set<string>();

    if (baseMessage) {
      detailSet.add(baseMessage);
    }

    this.collectMessagesFromTask(item).forEach((msg: string) => detailSet.add(msg));

    if (entry && entry.messages.length > 0) {
      entry.messages.forEach((msg: string) => detailSet.add(msg));
    }

    const primaryList = Array.from(detailSet);
    const additionalDetails = primaryList.filter((msg: string) => msg !== baseMessage && msg !== DEFAULT_DETAIL_MESSAGE);

    if (additionalDetails.length === 0) {
      if (!detailSet.has(DEFAULT_DETAIL_MESSAGE)) {
        detailSet.add(DEFAULT_DETAIL_MESSAGE);
      }
    }

    return Array.from(detailSet).join("\n");
  }

  private shouldFetchHataDetay(task: ITask): boolean {
    const status: any = task.status;
    if (status === this.taskDurum.BITTI_EKSIK || status === this.taskDurum.BITTI_HATALI) {
      return true;
    }
    if (typeof status === "string" && status.toUpperCase() === "X") {
      return true;
    }
    return false;
  }

  private async ensureHataMesajlari(task: ITask): Promise<void> {
    if (!task || !task.id) {
      return;
    }

    if (this.hataDetayPromises[task.id]) {
      await this.hataDetayPromises[task.id];
      return;
    }

    const existing = this.hataDetayState[task.id];
    if (existing && !existing.loading && existing.messages.length > 0) {
      return;
    }

    this.$set(this.hataDetayState, task.id, {
      loading: true,
      messages: existing ? existing.messages : []
    });

    const fetchPromise = this.fetchHataMesajlariFromServer(task);
    this.hataDetayPromises[task.id] = fetchPromise;

    try {
      const messages = await fetchPromise;
      this.$set(this.hataDetayState, task.id, {
        loading: false,
        messages
      });
    } catch (error) {
      console.error(`Görev (${task.id}) hata detayları alınamadı`, error);
      const fallbackMessages = this.collectMessagesFromTask(task);
      this.$set(this.hataDetayState, task.id, {
        loading: false,
        messages: fallbackMessages
      });
    } finally {
      delete this.hataDetayPromises[task.id];
    }
  }

  private async fetchHataMesajlariFromServer(task: ITask): Promise<string[]> {
    const messageSet = new Set<string>(this.collectMessagesFromTask(task));

    const appendChildMessages = (childTasks: Array<any>) => {
      if (!Array.isArray(childTasks)) {
        return;
      }
      childTasks.forEach((child: any) => {
        this.collectMessagesFromTask(child).forEach((msg: string) => messageSet.add(msg));
        if (child && Array.isArray(child.childTasks) && child.childTasks.length > 0) {
          appendChildMessages(child.childTasks);
        }
      });
    };

    if (!task.id) {
      return Array.from(messageSet);
    }

    const response = await this.$http.get(`/api/v1/task/${task.id}/child-tasks`);
    const childTasks: Array<any> = Array.isArray(response.data) ? response.data : [];
    appendChildMessages(childTasks);

    return Array.from(messageSet);
  }

  private collectMessagesFromTask(rawTask: any): string[] {
    if (!rawTask) {
      return [];
    }

    const messagesSet = new Set<string>();
    const pushMessage = (value: any) => {
      if (value === undefined || value === null) {
        return;
      }
      const text = String(value).replace(/\s+/g, " ").trim();
      if (!text) {
        return;
      }
      messagesSet.add(text);
    };

    pushMessage(rawTask.description);
    pushMessage(rawTask.aciklama);
    pushMessage(rawTask.error);
    pushMessage(rawTask.message);
    pushMessage(rawTask.result);
    pushMessage(rawTask.resultMessage);
    pushMessage(rawTask.result_description);

    this.collectMessagesFromResponse(rawTask.response, pushMessage, new Set<any>());

    return Array.from(messagesSet);
  }

  private collectMessagesFromResponse(value: any, push: (msg: string) => void, visited: Set<any>): void {
    if (value === undefined || value === null) {
      return;
    }

    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      push(value);
      return;
    }

    if (visited.has(value)) {
      return;
    }

    if (Array.isArray(value)) {
      visited.add(value);
      value.forEach((item: any) => this.collectMessagesFromResponse(item, push, visited));
      return;
    }

    if (typeof value === "object") {
      visited.add(value);
      const singleValueKeys = [
        "message",
        "error",
        "description",
        "detail",
        "reason",
        "cause",
        "info",
        "resultMessage",
        "resultDescription",
        "result"
      ];

      singleValueKeys.forEach((key: string) => {
        if (key in value) {
          const candidate = (value as any)[key];
          if (typeof candidate === "string" || typeof candidate === "number" || typeof candidate === "boolean") {
            push(candidate);
          }
        }
      });

      if (Array.isArray((value as any).hataliSorguAlanlari)) {
        (value as any).hataliSorguAlanlari.forEach((entry: any) => {
          if (!entry) {
            return;
          }
          if (typeof entry === "string") {
            push(entry);
            return;
          }
          const label = typeof entry.label === "string"
              ? entry.label.trim()
              : (typeof entry.name === "string" ? entry.name.trim() : "");
          const msg = typeof entry.message === "string"
              ? entry.message.trim()
              : (typeof entry.error === "string" ? entry.error.trim() : "");
          if (label && msg) {
            push(`${label}: ${msg}`);
          } else if (label) {
            push(label);
          } else if (msg) {
            push(msg);
          }
        });
      }

      const nestedKeys = [
        "messages",
        "errors",
        "details",
        "reasons",
        "hataliSorguAlanlari",
        "hatalar"
      ];

      nestedKeys.forEach((key: string) => {
        if (key in value) {
          this.collectMessagesFromResponse((value as any)[key], push, visited);
        }
      });

      if ("data" in value) {
        this.collectMessagesFromResponse((value as any).data, push, visited);
      }
    }
  }

  private getStatusKey(status: any): string {
    if (status === this.taskDurum.BITTI_BASARILI) {
      return "BITTI_BASARILI";
    }
    if (status === this.taskDurum.BITTI_EKSIK) {
      return "BITTI_EKSIK";
    }
    if (status === this.taskDurum.BITTI_HATALI) {
      return "BITTI_HATALI";
    }
    if (typeof status === "string") {
      return status.toUpperCase();
    }
    return String(status);
  }

  selectAll() {
    if (this.silinecekTasks.length == this.tasks.length) {
      this.silinecekTasks = []
    } else {
      this.silinecekTasks = [...this.tasks];
    }
  }

  async downloadImzali(task: ITask) {
    await this.$helper.download(TaskTuruLabel[(task.task_type_id as TaskTuru)] + new Date(task.updated_at).toISOString().substr(0, 10) + '.zip', '/api/v1/download-zip/' + task.id);
  }

  async downloadHtml(task: ITask, type: 'html' | 'word' | 'pdf') {
    if (type == "html")
      await this.$helper.download('HTML Sonuçlar.zip', '/api/v1/export-to-html/' + task.id);
    else if (type == "pdf")
      await this.$helper.download('PDF Sonuçlar.zip', '/api/v1/export-to-pdf/' + task.id);
    else if (type == "word")
      await this.wordDownload(task);
  }

  async wordDownload(task: ITask) {
    let zip = new JSZip();
    let childTasks: Array<ITask> = (await this.$http.get('/api/v1/task/' + task.id + '/child-tasks')).data;
    for (const cTask of childTasks) {
      let path = (cTask.birim_adi + " - " + cTask.dosya_esas_no).replace('/', '_');
      let sorgulananAlanlar = cTask.data.sorguAlanlari.map((item: any) => item.name);
      let hataliAlanlar = cTask.response.data.hataliSorguAlanlari.map((item: any) => item.name);
      let header = "<html xmlns:o='urn:schemas-microsoft-com:office:office' " +
          "xmlns:w='urn:schemas-microsoft-com:office:word' " +
          "xmlns='http://www.w3.org/TR/REC-html40'>" +
          "<head><meta charset='utf-8'><title>Save File</title></head><body>";
      let footer = "</body></html>";
      // dosya alanaları
      if (hataliAlanlar.indexOf('dosyahesap') == -1 && sorgulananAlanlar.indexOf('dosyahesap') > 0) {
        let sourceHTML = header + cTask.response.data.dosya.dosyaHesapBilgileri.html + footer;
        const file = new Blob([sourceHTML], {type: 'application/vnd.ms-word;charset=utf-8'});
        zip.file(path + "/Dosya Hesap Bilgileri.doc", file);
      }
      if (hataliAlanlar.indexOf('tahred') == -1 && sorgulananAlanlar.indexOf('tahred') > 0) {
        let sourceHTML = header + cTask.response.data.dosya.tahsilatReddiyat.html + footer;
        const file = new Blob([sourceHTML], {type: 'application/vnd.ms-word;charset=utf-8'});
        zip.file(path + "/Tahsilat Reddiyat.doc", file);
      }
      // borçlu alanları
      for (let borclu of cTask.response.data.borclular.items) {
        let borcluPath = "/" + (borclu.kurumAdi ? borclu.kurumAdi : (borclu.adi + " " + borclu.soyadi));
        if (hataliAlanlar.indexOf('postaceki') == -1 && sorgulananAlanlar.indexOf('postaceki') > 0) {
          let sourceHTML = header + borclu.postaCeki.html + footer;
          const file = new Blob([sourceHTML], {type: 'application/vnd.ms-word;charset=utf-8'});
          zip.file(path + borcluPath + "/Posta Çeki.doc", file);
        }
        if (hataliAlanlar.indexOf('bankahesap') == -1 && sorgulananAlanlar.indexOf('bankahesap') > 0) {
          let sourceHTML = header + borclu.bankaBilgileri.html + footer;
          const file = new Blob([sourceHTML], {type: 'application/vnd.ms-word;charset=utf-8'});
          zip.file(path + borcluPath + "/Banka Hesap Bilgileri.doc", file);
        }
        if (hataliAlanlar.indexOf('takbis') == -1 && sorgulananAlanlar.indexOf('takbis') > 0) {
          let sourceHTML = header + borclu.takbis.html + footer;
          const file = new Blob([sourceHTML], {type: 'application/vnd.ms-word;charset=utf-8'});
          zip.file(path + borcluPath + "/Takbis.doc", file);
        }
        if (hataliAlanlar.indexOf('dib') == -1 && sorgulananAlanlar.indexOf('dib') > 0) {
          let sourceHTML = header + borclu.dib.html + footer;
          const file = new Blob([sourceHTML], {type: 'application/vnd.ms-word;charset=utf-8'});
          zip.file(path + borcluPath + "/Dış İşleri Bilgileri.doc", file);
        }
        if (hataliAlanlar.indexOf('gib') == -1 && sorgulananAlanlar.indexOf('gib') > 0) {
          let sourceHTML = header + borclu.gib.html + footer;
          const file = new Blob([sourceHTML], {type: 'application/vnd.ms-word;charset=utf-8'});
          zip.file(path + borcluPath + "/GİB.doc", file);
        }
        if (hataliAlanlar.indexOf('gsm') == -1 && sorgulananAlanlar.indexOf('gsm') > 0) {
          let sourceHTML = header + borclu.gsm.html + footer;
          const file = new Blob([sourceHTML], {type: 'application/vnd.ms-word;charset=utf-8'});
          zip.file(path + borcluPath + "/GSM.doc", file);
        }
        if (hataliAlanlar.indexOf('iski') == -1 && sorgulananAlanlar.indexOf('iski') > 0) {
          let sourceHTML = header + borclu.iski.html + footer;
          const file = new Blob([sourceHTML], {type: 'application/vnd.ms-word;charset=utf-8'});
          zip.file(path + borcluPath + "/İSKİ.doc", file);
        }
        if (hataliAlanlar.indexOf('mernis') == -1 && sorgulananAlanlar.indexOf('mernis') > 0) {
          let sourceHTML = header + borclu.mersis.html + footer;
          const file = new Blob([sourceHTML], {type: 'application/vnd.ms-word;charset=utf-8'});
          zip.file(path + borcluPath + "/Mernis.doc", file);
        }
        if (hataliAlanlar.indexOf('sgkhaciz') == -1 && sorgulananAlanlar.indexOf('sgkhaciz') > 0) {
          let sourceHTML = header + borclu.sgkhaciz.html + footer;
          const file = new Blob([sourceHTML], {type: 'application/vnd.ms-word;charset=utf-8'});
          zip.file(path + borcluPath + "/SGK Haciz.doc", file);
        }
        if (hataliAlanlar.indexOf('icradosyakaydi') == -1 && sorgulananAlanlar.indexOf('icradosyakaydi') > 0) {
          let sourceHTML = header + borclu.icraDosyaKaydi.html + footer;
          const file = new Blob([sourceHTML], {type: 'application/vnd.ms-word;charset=utf-8'});
          zip.file(path + borcluPath + "/İcra Dosya Kaydı.doc", file);
        }
        if (hataliAlanlar.indexOf('egm') == -1 && sorgulananAlanlar.indexOf('egm') > 0) {
          let sourceHTML = header + borclu.egm.html + footer;
          const file = new Blob([sourceHTML], {type: 'application/vnd.ms-word;charset=utf-8'});
          zip.file(path + borcluPath + "/EGM.doc", file);
        }
      }
    }
    let contents = await zip.generateAsync({type: "blob"})
    FileSaver.saveAs(contents, 'Word Sorgu Sonuçları ' + new Date(task.updated_at).toISOString().substr(0, 10));
  }

  async download(task: ITask) {
    if (task.task_type_id == TaskTuru.mts_odeme)
      await this.$helper.download('Excel Sonuçlar.xlsx', '/api/v1/mts-odeme-export-excel/' + task.id);
    else if (task.task_type_id == TaskTuru.harc_masraf_ode)
      await this.$helper.download('Excel Sonuçlar.xlsx', '/api/v1/harc-masraf-odeme-export-excel/' + task.id);
    else if (task.task_type_id == TaskTuru.evrak_gonder)
      await this.$helper.download('Excel Sonuçlar.xlsx', '/api/v1/evrak-gonder-export-excel/' + task.id);
    else if (task.task_type_id == TaskTuru.mts_tebligat_gonder)
      await this.$helper.download('Excel Sonuçlar.xlsx', '/api/v1/mts-tebligat-gonder-export-excel/' + task.id);
    else if (task.task_type_id == TaskTuru.makbuz_indir) {
      if (task.status==TaskDurum.BITTI_HATALI)
        this.$toast.warning("Makbuzlar indirilemedi. Evrak bulunamadı veya yetkiniz yoktur.");
      else
        await this.downloadImzali(task);
    }
    else if (task.task_type_id == TaskTuru.evrak_indir || task.task_type_id == TaskTuru.hazir_evrak_gonder)
      await this.downloadImzali(task);
    else
      await this.$helper.download('Excel Sonuçlar.xlsx', '/api/v1/export-to-excel/' + task.id);
  }

  async downloadTopluTT(task: ITask) {
    this.disabledDownloadIds.push(task.id);
    try {
      let childTasksRes = await this.$http.get('/api/v1/task/' + task.id + '/child-tasks');
      let childTasks = childTasksRes.data.filter((item: ITask) => item.status == this.taskDurum.BITTI_BASARILI);
      if (childTasks.length > 0) {
        let params: Array<{
          birimId: string,
          acilisTime: number
        }> = childTasks.map((item: ITask) => ({
          birimId: item.response.tevziBilgileri.birimID,
          acilisTime: new Date(this.tarihiFormatla(item.response.tevziBilgileri.dosyaAcilisTarihi)).getTime(),
          dosyaAcilisTarihi: item.response.tevziBilgileri.dosyaAcilisTarihi
        }));
        let dosyalar: Array<TamamlanmayanDosyalarRow> = await this.$uyap.TamamlanmayanDosyaBul().runToplu(params);
        let uyapIdleri = dosyalar.map((item: TamamlanmayanDosyalarRow) => ({
          dosyaId: item.dosyaID,
          birimAdi: item.birimAdi,
          esasNo: item.dosyaNo ?? ''
        }));
        if (uyapIdleri.length > 0)
          await this.$uyapHelper.topluTakipTalebiIndir(uyapIdleri);
        else
          throw new Error("Dosyalar bulunamadı.");
      } else {
        this.$toast.warning("Tamamlanan işlem bulunmadığı için toplu takip talebi oluşturulamıyor.")
      }
    } catch (e) {
      this.$toast.error("Takip talepleri indirilirken sorun oluştu. " + e.message);
      console.error('topluTTIndir Error', e.message);
    } finally {
      this.disabledDownloadIds = this.disabledDownloadIds.filter(id => id !== task.id)
    }
  }

  tarihiFormatla(tarih: string) {
    // dateStr -> 09/05/2024 10:43:06
    const parts = tarih.split(' ');             // Boşluklardan böl
    const dateParts = parts[0].split('/');      // Tarihi '/' işaretlerinden böl
    const timeParts = parts[1].split(':');      // Saati ':' işaretlerinden böl
    tarih = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}T${timeParts.join(':')}`;
    return tarih;
  }

  onDelete(task: ITask) {
    this.$store.dispatch('deleteAllTasks', task);
  }

  async onDeleteMultiple() {
    try {
      this.deleting = true;
      this.isDeleteModeActive = false;
      await this.$http.post('/api/v1/task/toplu-sil', {tasks_ids: this.silinecekTasks.map((item: ITask) => item.id)});
      this.silinecekTasks = [];
    } catch (e) {
      this.$toast.error('Silme işleminde hata oluştu. ' + e.message);
    } finally {
      await this.$store.dispatch('getTasksFromServer');
      this.deleting = false;
    }
  }

  detayaGit(task: ITask) {
    switch (task.task_type_id) {
      case this.taskTuru.takip_hazirla:
        this.$router.push(ProgramPaths.eicrapro + '/takip-hazirla-detay/' + task.id);
        break;
      case this.taskTuru.hizli_takip_ac:
      case this.taskTuru.takip_ac:
        this.$router.push(ProgramPaths.eicrapro + '/takip-ac-detay/' + task.id);
        break;
      case this.taskTuru.mts_takip_hazirla:
        this.$router.push(ProgramPaths.eicrapro + '/mts-takip-hazirla-beta-detay/' + task.id);
        break;
      case this.taskTuru.eski_mts_hizli_takip_ac:
        this.$router.push(ProgramPaths.eicrapro + '/mts-takip-hazirla-detay/' + task.id);
        break;
      case this.taskTuru.mts_hizli_takip_ac:
      case this.taskTuru.mts_takip_ac:
        this.$router.push(ProgramPaths.eicrapro + '/mts-takip-ac-detay/' + task.id);
        break;
      case this.taskTuru.takip_sorgula:
        this.$router.push(ProgramPaths.eicrapro + '/takip-sorgula-detay/' + task.id);
        break;
      case this.taskTuru.evrak_gonder:
        this.$router.push(ProgramPaths.eicrapro + '/evrak-gonder-detay/' + task.id);
        break;
      case this.taskTuru.hazir_evrak_gonder:
        this.$router.push(ProgramPaths.eicrapro + '/hazir-evrak-gonder-detay/' + task.id);
        break;
      case this.taskTuru.makbuz_indir:
        this.$router.push(ProgramPaths.eicrapro + '/makbuz-indir-detay/' + task.id);
        break;
      case this.taskTuru.toplu_genel_talep_hazirla:
        this.$router.push(ProgramPaths.eicrapro + '/genel-talep-gonder-detay/' + task.id);
        break;
      case this.taskTuru.toplu_tebligat_hazirla:
        this.$router.push(ProgramPaths.eicrapro + '/tebligat-gonder-detay/' + task.id);
        break;
      case this.taskTuru.toplu_haciz_gonder:
        this.$router.push(ProgramPaths.eicrapro + '/haciz-gonder-detay/' + task.id);
        break;
      case this.taskTuru.harc_masraf_ode:
        this.$router.push(ProgramPaths.eicrapro + '/harc-masraf-ode-detay/' + task.id);
        break;
      case this.taskTuru.mts_odeme:
        this.$router.push(ProgramPaths.eicrapro + '/mts-ode-detay/' + task.id);
        break;
      case this.taskTuru.mts_tebligat_gonder:
        this.$router.push(ProgramPaths.eicrapro + '/mts-tebligat-gonder-detay/' + task.id);
        break;
      case this.taskTuru.evrak_indir:
        this.$router.push(ProgramPaths.eicrapro + '/evrak-indir-detay/' + task.id);
        break;
    }
  }
}
